import { Box, HStack, Icon, Stack, Text } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import CrownIcon from '~icons/lucide/crown';
import { useState } from 'react';

import { ChatList } from '#modules/chat/components/Chats/ChatList';
import type { Chat, ChatTab } from '#modules/chat/components/Chats/hooks/useChats';
import { GameLogo } from '#modules/finder/features/posts/components/SessionParts';
import { Panel, PeopleIcon } from '#features/ui';
import { Avatar } from '#features/ui/components/Avatar';

const GAME_ID = '77777777-7777-4777-8777-777777777777';

/** Показывает настоящий список чатов с примерными данными, не требуя входа в аккаунт. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/session-chats')({
  component: SessionChatsPreview,
  ssr: false,
  loader: ({ context }) => {
    context.queryClient.setQueryData(
      ['games'],
      [
        {
          __typename: 'Game',
          id: GAME_ID,
          name: 'Apex Legends',
          slug: 'apex-legends',
          icon: '/preview/session-chats/apex-mark.svg',
          iconColor: 'fg.warning',
        },
      ]
    );
  },
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
});

const VIEWER_ID = '11111111-1111-4111-8111-111111111111';
const HOST_ID = '22222222-2222-4222-8222-222222222222';
const GUEST_ID = '33333333-3333-4333-8333-333333333333';
const GAME_ICON = '/preview/session-chats/apex-mark.svg';
const hostBadgeCutout = { x: 42, y: 42, radius: 14 };
const anotherDay = (days: number, hour: number) => {
  const value = new Date();
  value.setDate(value.getDate() + days);
  value.setHours(hour, 0, 0, 0);
  return value.toISOString();
};

type ChatCase = {
  index: number;
  gameId: string | null;
  startedAt: string;
  expiredAt: string;
  hostIsViewer: boolean;
  lastMessageText: string;
  unreadCount?: number;
};

const makeChat = ({
  index,
  gameId,
  startedAt,
  expiredAt,
  hostIsViewer,
  lastMessageText,
  unreadCount = 0,
}: ChatCase): Chat =>
  ({
    id: `44444444-4444-4444-8444-44444444444${index}`,
    kind: 'SESSION',
    gameId,
    sessionStartedAt: startedAt,
    sessionExpiredAt: expiredAt,
    host: {
      userId: hostIsViewer ? VIEWER_ID : HOST_ID,
      userData: {
        id: hostIsViewer ? VIEWER_ID : HOST_ID,
        displayName: hostIsViewer ? 'Вы' : 'scB971017',
      },
    },
    members: [
      { userId: VIEWER_ID, userData: { id: VIEWER_ID, displayName: 'Артём', avatar: null } },
      { userId: GUEST_ID, userData: { id: GUEST_ID, displayName: 'Nova', avatar: null } },
    ],
    lastMessageText,
    lastMessageAt: index < 3 ? new Date().toISOString() : expiredAt,
    updatedAt: index < 3 ? new Date().toISOString() : expiredAt,
    unreadCount,
  }) as unknown as Chat;

const HostPreviewRow = ({ host, explicitHost }: { host: 'Salt' | 'Вы'; explicitHost: boolean }) => (
  <HStack gap={3} px={3} py={2.5} w="100%" alignItems="center">
    <Box position="relative" flexShrink={0}>
      <GameLogo
        icon={GAME_ICON}
        color="fg.warning"
        diameter={48}
        status="over"
        overlayCutout={hostBadgeCutout}
      />
      <Avatar
        position="absolute"
        right="-6px"
        bottom="-6px"
        size="2xs"
        name={host === 'Вы' ? 'Артём' : host}
        aria-label={`Автор заявки: ${host}`}
      />
    </Box>
    <Stack gap={0.5} minW={0} flex={1}>
      <HStack gap={1} w="100%" minW={0}>
        <Text textStyle="nav" whiteSpace="nowrap" flexShrink={0}>
          24 сент., 19:00
        </Text>
        {!explicitHost && (
          <>
            <Text textStyle="nav" color="fg.subtle">
              ·
            </Text>
            <Text textStyle="nav" truncate minW={0}>
              {host}
            </Text>
          </>
        )}
        <Text textStyle="caption" color="fg.muted" ml="auto" flexShrink={0}>
          24 сент.
        </Text>
      </HStack>
      {explicitHost && (
        <HStack gap={1} color="fg.secondary">
          <Icon boxSize={3} color="fg.warning" aria-hidden="true">
            <CrownIcon />
          </Icon>
          <Text textStyle="caption">Автор: {host}</Text>
        </HStack>
      )}
      <HStack gap={1} color="fg.muted" minW={0}>
        <Icon boxSize={3.5} aria-hidden="true">
          <PeopleIcon />
        </Icon>
        <Text textStyle="caption">2</Text>
        <Text textStyle="caption">·</Text>
        <Text textStyle="body" truncate minW={0}>
          Сессия завершена
        </Text>
      </HStack>
    </Stack>
  </HStack>
);

const HostConcept = ({
  explicitHost,
  title,
  note,
}: {
  explicitHost: boolean;
  title: string;
  note: string;
}) => (
  <Panel variant="object" w="340px" maxW="100%" p={3}>
    <Stack gap={1} mb={3} px={2}>
      <Text textStyle="section">{title}</Text>
      <Text textStyle="caption" color="fg.secondary">
        {note}
      </Text>
    </Stack>
    <HostPreviewRow host="Salt" explicitHost={explicitHost} />
    <HostPreviewRow host="Вы" explicitHost={explicitHost} />
  </Panel>
);

function SessionChatsPreview() {
  const [tab, setTab] = useState<ChatTab>('ALL');
  const chats = [
    makeChat({
      index: 1,
      gameId: GAME_ID,
      startedAt: anotherDay(1, 20),
      expiredAt: anotherDay(1, 22),
      hostIsViewer: false,
      lastMessageText: 'Привет! Я буду к восьми',
      unreadCount: 1,
    }),
    makeChat({
      index: 2,
      gameId: GAME_ID,
      startedAt: anotherDay(0, 11),
      expiredAt: anotherDay(0, 23),
      hostIsViewer: true,
      lastMessageText: 'Залетай в голос',
    }),
    makeChat({
      index: 3,
      gameId: GAME_ID,
      startedAt: anotherDay(-1, 19),
      expiredAt: anotherDay(-1, 21),
      hostIsViewer: true,
      lastMessageText: 'SESSION_FINISHED',
    }),
    makeChat({
      index: 4,
      gameId: GAME_ID,
      startedAt: anotherDay(-3, 20),
      expiredAt: anotherDay(-3, 22),
      hostIsViewer: true,
      lastMessageText: 'SESSION_FINISHED',
    }),
    makeChat({
      index: 5,
      gameId: GAME_ID,
      startedAt: anotherDay(-6, 18),
      expiredAt: anotherDay(-6, 20),
      hostIsViewer: true,
      lastMessageText: 'SESSION_FINISHED',
    }),
  ];

  return (
    <Stack alignItems="center" gap={6} px={4} py={10}>
      <Stack gap={2} maxW="640px" textAlign="center">
        <Text textStyle="heading">Как показать автора заявки</Text>
        <Text textStyle="body" color="fg.secondary">
          Два макета со значком игры и аватаром автора. Число игроков остаётся у последнего
          сообщения.
        </Text>
      </Stack>
      <HStack gap={4} flexWrap="wrap" justifyContent="center" alignItems="start">
        <HostConcept
          explicitHost={false}
          title="А. Аватар на логотипе · выбран"
          note="Игра остаётся главным знаком; автор виден в углу."
        />
        <HostConcept
          explicitHost
          title="Б. Аватар и подпись"
          note="Роль названа словами; строка становится выше."
        />
      </HStack>
      <Text textStyle="section" pt={4}>
        Вариант А в настоящем списке
      </Text>
      <Box w="340px" maxW="100%" minH="540px" borderWidth="1px" borderColor="border.subtle">
        <ChatList
          chats={tab === 'DIRECT' ? [] : chats}
          chatsStatus="success"
          chatId={undefined}
          hasNoChats={tab === 'DIRECT'}
          hasMoreChats={false}
          isLoadingMoreChats={false}
          loadMoreChats={() => undefined}
          isMobileThread={false}
          tab={tab}
          onTabChange={setTab}
          hasTabs
          viewerId={VIEWER_ID}
          chatCounts={{
            all: chats.length,
            direct: 0,
            session: chats.length,
            unreadAll: 1,
            unreadDirect: 0,
            unreadSession: 1,
          }}
        />
      </Box>
    </Stack>
  );
}
